/**
 * Firebase Configuration
 * 
 * Initializes Firebase app, authentication, and Firestore database.
 * Configures Google OAuth provider with additional scopes for user info.
 * 
 * Note: CapacitorHttp is disabled globally (see capacitor.config.ts) so that
 * Firestore's WebChannel streaming works properly on native platforms.
 * For our own API calls, we use CapacitorHttp manually via nativeFetch()
 * or the axios adapter to bypass CORS.
 */

import { initializeApp, getApps, FirebaseApp, FirebaseOptions } from 'firebase/app';
import { getAuth, initializeAuth, GoogleAuthProvider, setPersistence, browserLocalPersistence, indexedDBLocalPersistence, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';
import { Capacitor } from '@capacitor/core';

// Helper to get env vars (supports both Vite and legacy CRA)
const getEnv = (key: string): string | undefined => {
  // Vite uses import.meta.env
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return (import.meta.env as unknown as Record<string, string>)[key];
  }
  // Fallback to process.env for legacy/SSR (only if process is defined)
  if (typeof process !== 'undefined' && process.env) {
    return (process.env as Record<string, string>)[key];
  }
  return undefined;
};

// Type for Firebase config with optional fields
interface FirebaseConfig extends FirebaseOptions {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  measurementId?: string;
}

// Build Firebase config from environment variables
const firebaseConfig: FirebaseConfig = {
  apiKey: getEnv('VITE_FIREBASE_API_KEY') ?? '',
  authDomain: getEnv('VITE_FIREBASE_AUTH_DOMAIN') ?? '',
  projectId: getEnv('VITE_FIREBASE_PROJECT_ID') ?? '',
};

// Add optional config fields if present
const storageBucket = getEnv('VITE_FIREBASE_STORAGE_BUCKET');
if (storageBucket) {
  firebaseConfig.storageBucket = storageBucket;
}

const messagingSenderId = getEnv('VITE_FIREBASE_MESSAGING_SENDER_ID');
if (messagingSenderId) {
  firebaseConfig.messagingSenderId = messagingSenderId;
}

const appId = getEnv('VITE_FIREBASE_APP_ID');
if (appId) {
  firebaseConfig.appId = appId;
}

const measurementId = getEnv('VITE_FIREBASE_MEASUREMENT_ID');
if (measurementId) {
  firebaseConfig.measurementId = measurementId;
}

// Initialize Firebase app (reuse existing instance if available)
const existingApp = getApps().length > 0;
const app: FirebaseApp = existingApp ? getApps()[0] : initializeApp(firebaseConfig);

// Initialize Firestore database
// Note: CapacitorHttp is disabled globally so Firestore WebChannel works normally.
// We use the default getFirestore() which is compatible with all platforms.
const db: Firestore = getFirestore(app);

// Initialize Firebase Authentication
// On native platforms, use initializeAuth with indexedDBLocalPersistence per capacitor-firebase docs:
// https://github.com/capawesome-team/capacitor-firebase/blob/main/packages/authentication/docs/firebase-js-sdk.md
// 
// IMPORTANT: initializeAuth can only be called once per app. If called again it throws an error.
// We track if we created a new app instance to know whether to call initializeAuth or getAuth.
let auth: Auth;
if (Capacitor.isNativePlatform()) {
  // On native, we need indexedDBLocalPersistence for session to persist across app restarts
  if (existingApp) {
    // App already exists, auth was already initialized with persistence
    auth = getAuth(app);
    console.log('📱 Reusing existing Firebase Auth instance');
  } else {
    // Fresh app, initialize auth with proper persistence
    auth = initializeAuth(app, {
      persistence: indexedDBLocalPersistence,
    });
    console.log('📱 Initialized Firebase Auth with indexedDBLocalPersistence');
  }
} else {
  auth = getAuth(app);
  // Set persistence to local for web (keeps user logged in across browser sessions)
  setPersistence(auth, browserLocalPersistence).catch((error: Error) => {
    console.warn('Failed to set auth persistence:', error);
  });
}

// Configure Google OAuth provider
const googleProvider = new GoogleAuthProvider();

// Request additional user info: age, gender, birthday
// Note: These require user consent and Google OAuth app verification for production
googleProvider.addScope('https://www.googleapis.com/auth/user.birthday.read');
googleProvider.addScope('https://www.googleapis.com/auth/user.gender.read');
googleProvider.addScope('profile');
googleProvider.addScope('email');

// Custom parameters for better consent flow
googleProvider.setCustomParameters({
  prompt: 'select_account',
  include_granted_scopes: 'true'
});

// Initialize Firebase Analytics (GA4) — web only, async check for browser support.
// Exported as a Promise so callers can await readiness before logging events.
let analytics: Analytics | null = null;
const analyticsReady: Promise<Analytics | null> =
  !Capacitor.isNativePlatform() && firebaseConfig.measurementId
    ? isSupported()
        .then((supported) => {
          if (supported) {
            analytics = getAnalytics(app);
          }
          return analytics;
        })
        .catch(() => {
          // Analytics not supported in this environment (SSR, test, etc.)
          return null;
        })
    : Promise.resolve(null);

export { app, auth, googleProvider, db, analytics, analyticsReady };
